import React from "react";
import { Box, BoxProps, Flex, Image, Text } from "@chakra-ui/react";
import { useLandingCopy } from "../../LandingLanguageContext";

// The app's own multiple-choice colors (src/constants/GlobalStyles.ts), so the snippet reads as
// a real Jungle question rather than a themed illustration of one.
const JUNGLE_QUESTION_UI = {
  stemBg: "#E4FFD1",
  stemBorder: "#D2EBC0",
  optionBg: "#FFFFFF",
  optionBorder: "#E1EBE5",
  correctBg: "#EDFFF2",
  correctBorder: "#99E1AD",
  text: "#3D3E3D",
  font: '"Outfit", sans-serif',
};

// The slide is the largest thing above the fold, so the browser is asked to fetch it before the rest.
// React 18 forwards the attribute only in this lowercase spelling (React 19 knows `fetchPriority`).
const HERO_IMAGE_FETCH_PRIORITY = { fetchpriority: "high" } as Record<string, string>;

/**
 * The slide's file and its phone-sized copy (made with `cwebp -resize 800 0`, a third of the bytes). On a
 * phone in the Clearing design the slide is the page's largest contentful paint, so how soon it arrives is
 * the page's LCP. `sizes` follows the hero layout: the slide is its column minus the question card's
 * offset, plus the cropped edge. scripts/generate-marketing-static-html.mjs preloads the same candidates
 * in the home pages' <head> (through src/marketing-prerender.tsx), so the download starts with the fonts
 * instead of after the whole page's HTML.
 */
export const LECTURE_SLIDE_IMAGE_SOURCES = {
  src: "/images/landing/slides/lumen-biology-small-intestine.webp",
  srcSet:
    "/images/landing/slides/lumen-biology-small-intestine-w800.webp 800w, /images/landing/slides/lumen-biology-small-intestine.webp 1600w",
  sizes: "(min-width: 62em) 460px, (min-width: 48em) 540px, calc(100vw - 70px)",
};

const LECTURE_SLIDE = {
  // The deck's template has a dark grey bar down the left edge; crop it off by oversizing the image.
  cropLeft: "5.3%",
  // Where the sentence the question was drawn from sits on the cropped slide, as fractions of its size.
  markedSentence: { left: "2.4%", top: "27%", width: "73%", height: "7.5%" },
};

// The question's wording lives in landingCopy.ts (exampleQuestion); every language lists the options
// in the same order, so "Small intestine" is the second one in all of them.
const CORRECT_OPTION_INDEX = 1;

// A real lecture slide (Lumen Learning, CC BY) with the source sentence marked, and the Jungle
// question generated from it laid over the slide in the app's own question UI.
export function LectureSlideToJungleQuestion({ onLight = false, ...props }: BoxProps & { onLight?: boolean }) {
  const exampleQuestionCopy = useLandingCopy().exampleQuestion;
  const slideShadow = onLight ? "0 16px 40px rgba(20, 40, 16, 0.14)" : "0 20px 50px rgba(0, 0, 0, 0.45)";
  const questionShadow = onLight ? "0 24px 56px rgba(20, 40, 16, 0.18)" : "0 32px 64px rgba(0, 0, 0, 0.5)";
  return (
    <Box position="relative" pt={{ base: "132px", md: "236px" }} pl={{ base: "40px", md: "84px" }} {...props}>
      <Box
        aria-hidden="true"
        position="absolute"
        top={0}
        left={0}
        right={{ base: "48px", md: "56px" }}
        borderRadius="12px"
        overflow="hidden"
        boxShadow={slideShadow}
        bg="#F4F7FB"
      >
        {/* htmlWidth/htmlHeight are the <img> attributes (Chakra's width/height props are CSS, which w/h
            override): they give the slide its 16:9 box from the first paint instead of when the file
            arrives, which counted as a layout shift. */}
        <Image
          src={LECTURE_SLIDE_IMAGE_SOURCES.src}
          srcSet={LECTURE_SLIDE_IMAGE_SOURCES.srcSet}
          sizes={LECTURE_SLIDE_IMAGE_SOURCES.sizes}
          htmlWidth={1600}
          htmlHeight={900}
          h="auto"
          {...HERO_IMAGE_FETCH_PRIORITY}
          alt={exampleQuestionCopy.lectureSlideAlt}
          w={`calc(100% + ${LECTURE_SLIDE.cropLeft})`}
          maxW="none"
          ml={`-${LECTURE_SLIDE.cropLeft}`}
          display="block"
        />
        <Box
          position="absolute"
          left={LECTURE_SLIDE.markedSentence.left}
          top={LECTURE_SLIDE.markedSentence.top}
          w={LECTURE_SLIDE.markedSentence.width}
          h={LECTURE_SLIDE.markedSentence.height}
          bg="rgba(255, 230, 0, 0.42)"
          borderRadius="3px"
          mixBlendMode="multiply"
        />
      </Box>

      <Box
        position="relative"
        bg="#FFFFFF"
        borderRadius="20px"
        p={{ base: "16px", md: "34px" }}
        boxShadow={questionShadow}
        fontFamily={JUNGLE_QUESTION_UI.font}
        color={JUNGLE_QUESTION_UI.text}
      >
        <Box
          bg={JUNGLE_QUESTION_UI.stemBg}
          border={`1px solid ${JUNGLE_QUESTION_UI.stemBorder}`}
          borderRadius="14px"
          px={{ base: "14px", md: "18px" }}
          py={{ base: "12px", md: "16px" }}
          boxShadow="0px 2px 20px 0px rgba(0, 0, 0, 0.06), 0px 1px 2px 0px rgba(0, 0, 0, 0.05)"
        >
          <Text fontSize={{ base: "14px", md: "17px" }} lineHeight="124%">
            {exampleQuestionCopy.stem}
          </Text>
        </Box>
        <Flex direction="column" gap={{ base: "6px", md: "8px" }} mt={{ base: "10px", md: "14px" }} px={{ base: "2px", md: "6px" }}>
          {exampleQuestionCopy.options.map((option, optionIndex) => {
            const isCorrect = optionIndex === CORRECT_OPTION_INDEX;
            return (
              <Box
                key={option}
                px={{ base: "14px", md: "18px" }}
                py={{ base: "8px", md: "13px" }}
                borderRadius="14px"
                bg={isCorrect ? JUNGLE_QUESTION_UI.correctBg : JUNGLE_QUESTION_UI.optionBg}
                border={`1px solid ${isCorrect ? JUNGLE_QUESTION_UI.correctBorder : JUNGLE_QUESTION_UI.optionBorder}`}
                boxShadow={
                  isCorrect ? "0px 1.5px 1.5px 0px rgba(135, 225, 160, 0.32)" : "0px 1px 2px 0px rgba(8, 47, 19, 0.08)"
                }
              >
                <Text fontSize={{ base: "13.5px", md: "16px" }} lineHeight="124%">
                  {"ABCD"[optionIndex]}. {option}
                </Text>
              </Box>
            );
          })}
        </Flex>
      </Box>
    </Box>
  );
}
